import {
  GetConductEntriesAggregatedResponse,
  GetConductEntriesQueryFilters,
  GetConductEntriesQuerySort,
} from '@schooly/api';
import { GridBody, MainPageGrid, SkeletonRows } from '@schooly/style';
import React, { FC, ReactNode, useCallback, useState } from 'react';

import { ConductEntriesHeader, ConductStudentGroupRow, SKELETON_COLS } from './ConductGrid';
import { ConductStudentEntries } from './ConductStudentEntries';

export type ConductGroupedQueryResult = {
  data: GetConductEntriesAggregatedResponse | undefined;
  isLoading: boolean;
};

type ConductGroupedGridProps = {
  schoolId: string;
  filters: GetConductEntriesQueryFilters;
  sort?: GetConductEntriesQuerySort;
  canEdit: boolean;
  onChangeSort: (v: GetConductEntriesQuerySort) => void;
  headerRightIcon?: ReactNode;
  queryResult: ConductGroupedQueryResult;
};

export const ConductGroupedGrid: FC<ConductGroupedGridProps> = ({
  schoolId,
  filters,
  sort,
  canEdit,
  onChangeSort,
  headerRightIcon,
  queryResult,
}) => {
  const { data, isLoading } = queryResult;
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [hoverId, setHoverId] = useState<string | null>(null);

  const toggleExpand = useCallback((rowValue: string) => {
    setExpandedRows((prev) => ({ ...prev, [rowValue]: !prev[rowValue] }));
  }, []);

  const rows = data?.rows ?? [];

  return (
    <MainPageGrid mt={2.4}>
      <ConductEntriesHeader
        isGrouped
        sort={sort}
        onChangeSort={onChangeSort}
        rightIcon={headerRightIcon}
      />
      <GridBody>
        {rows.map((row, index) => {
          const isExpanded = !!expandedRows[row.value];
          const isLast = index === rows.length - 1;

          return (
            <React.Fragment key={row.value}>
              <ConductStudentGroupRow
                row={row}
                isExpanded={isExpanded}
                isLast={isLast}
                hoverIdStudentId={hoverId}
                onExpand={() => toggleExpand(row.value)}
                onMouseEnter={() => setHoverId(row.value)}
                onMouseLeave={() => setHoverId(null)}
              />
              {isExpanded && (
                <ConductStudentEntries
                  schoolId={schoolId}
                  relationId={row.value}
                  filters={filters}
                  canEdit={canEdit}
                  hoverId={hoverId}
                  onHover={setHoverId}
                  isLastStudent={isLast}
                  totalEntries={row.type_counts.reduce((sum, tc) => sum + tc.count, 0)}
                />
              )}
            </React.Fragment>
          );
        })}
        {isLoading && !rows.length && <SkeletonRows columnsCount={SKELETON_COLS} amount={10} />}
      </GridBody>
    </MainPageGrid>
  );
};
